// @flow
import React from "react";
import styled from "styled-components";
import { scrollToById } from "@nested/utils";
import { P, H6, H2, media, SmartLink } from "@nested/ui";
import {
  Calendar,
  Clock,
  Completion,
  EngageAgent,
  Exchange,
  Listing,
  ProceedableOffer,
} from "@nested/ui/icons";
import { daysToWeeks, type TimelineData } from "./utils";

const getIcon = (type: string) => {
  switch (type) {
    case "EngageAgent":
      return <EngageAgent />;
    case "Listing":
      return <Listing />;
    case "ProceedableOffer":
      return <ProceedableOffer />;
    case "Exchange":
      return <Exchange />;
    case "Completion":
      return <Completion />;
    case "Clock":
      return <Clock />;
    case "Calendar":
      return <Calendar />;
    default:
      return <Calendar />;
  }
};

const Content = styled.section`
  background-color: ${({ theme }) => theme.palette.white.medium};
`;

const Wrapper = styled.div`
  max-width: 500px;
  margin-right: auto;
  margin-left: auto;
  padding: 50px 10px;
  ${media.tablet`
    padding: 50px 0;
  `}
`;

const TimelineItems = styled.div`
  position: relative;
  height: ${({ exampleView }) => (exampleView ? "800px" : "auto")};
  overflow: hidden;
  vertical-align: top;
  max-width: 521px;
  margin: 0 auto;
`;

const FadeoutOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: ${({ exampleView }) => (exampleView ? "300px" : "100%")};
  width: 100%;
  background: linear-gradient(transparent, white);
  z-index: 10;
`;

const Line = styled.div`
  width: 10px;
  margin: 0 40px 0 25px;
  background-color: ${({ theme, showLine }) =>
    showLine ? theme.palette.misc.grey93 : "unset"};
  position: relative;
`;

const Icon = styled.div`
  position: absolute;
  top: 0;
  left: -25px;
  padding: 10px 0;
  background-color: ${({ theme }) => theme.palette.white.medium};
  svg {
    height: 60px;
  }
`;

const TimelineSidebar = ({ icon, showLine }) => (
  <Line showLine={showLine}>
    <Icon>{getIcon(icon)}</Icon>
  </Line>
);

const StageSection = styled.div`
  display: flex;
  flex-grow: 1;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`;

const Title = styled(H6)`
  height: 60px;
  line-height: 60px;
  padding-left: 20px;
  margin: 0;
`;

const Container = styled.div`
  background: ${({ theme, grey, date }) => {
    if (grey && date) return theme.palette.misc.grey95;
    if (date) return theme.palette.peachy.light;
    return "unset";
  }};
  padding: 10px 20px;
  border-radius: 4px;
`;

const RowWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Label = styled(P)`
  margin: 0;
`;

const SmallIcon = styled.div`
  svg {
    height: 22px;
  }
`;

const BoldText = styled.span`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.palette.navy.medium};
  line-height: 30px;
  margin-left: 10px;
`;

const InfoText = styled(P)`
  margin-top: 10px;
  padding-left: 20px;
`;

const InlineLink = styled(SmartLink)`
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.palette.navy.medium};
  text-decoration: underline;
  transition: 0.3s ease;
  &:hover {
    color: ${({ theme }) => theme.palette.misc.pinkPeach};
  }
`;

const DateSection = ({ date, dateText, exampleView }) => (
  <Container date grey={exampleView}>
    <Label left>{exampleView ? "Example Date:" : dateText}</Label>
    <RowWrapper>
      <SmallIcon>
        <Calendar />
      </SmallIcon>
      <BoldText data-test="timeline-date" className="chromatic-ignore">
        {date}
      </BoldText>
    </RowWrapper>
  </Container>
);

const DurationSection = ({ duration, durationText }) => (
  <Container>
    <Label left>{durationText}</Label>
    <RowWrapper>
      <SmallIcon>
        <Clock />
      </SmallIcon>
      <BoldText data-test="timeline-duration">
        {daysToWeeks(duration)} week{daysToWeeks(duration) > 1 && "s"}
      </BoldText>
    </RowWrapper>
  </Container>
);

const ButtonCTA = styled.div`
  cursor: pointer;
  padding: 20px 10px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  background-color: ${({ theme }) => theme.palette.misc.pinkPeach};
  box-shadow: 0 10px 10px -10px rgba(0, 0, 0, 0.05);
  max-width: 521px;
  margin: 0 auto;
  span {
    text-decoration: underline;
    margin-left: 20px;
  }
  ${media.tablet`
    padding: 20px;
  `}
`;

const ButtonText = styled(P)`
  margin-bottom: 0;
  text-decoration: underline;
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.palette.navy.medium};
  ${media.tablet`
    padding-left: 20px;
  `}
`;

const CalculateCTA = () => (
  <ButtonCTA onClick={() => scrollToById("title")}>
    <Calendar />
    <ButtonText>Calculate your personalised timeline</ButtonText>
  </ButtonCTA>
);

type StageProps = {
  date: string,
  dateText: string,
  duration?: number,
  durationText?: string,
  exampleView?: boolean,
  icon: string,
  linkToHomeText?: string,
  stageInformation: string,
  title: string,
};

const Stage = ({
  date,
  dateText,
  duration,
  durationText,
  exampleView,
  icon,
  linkToHomeText,
  stageInformation,
  title,
}: StageProps) => (
  <StageSection>
    <TimelineSidebar icon={icon} showLine={stageInformation} />
    <InfoContainer>
      <Title left>{title}</Title>
      <DateSection date={date} dateText={dateText} exampleView={exampleView} />
      {duration && (
        <DurationSection duration={duration} durationText={durationText} />
      )}
      {stageInformation && (
        <InfoText left>
          {stageInformation}{" "}
          {linkToHomeText && (
            <InlineLink target="_blank">{linkToHomeText}</InlineLink>
          )}
        </InfoText>
      )}
    </InfoContainer>
  </StageSection>
);

const getHeading = (exampleView, listDateInPast) => {
  if (exampleView) return "A typical London timeline";
  if (listDateInPast) return "A typical timeline if you were to list today";
  return "Your home sale timeline";
};

type Props = {
  exampleView?: boolean,
  timelineData: TimelineData,
  listDateInPast: boolean,
};

const Timeline = ({
  exampleView,
  listDateInPast,
  timelineData: { dates, duration },
}: Props) => (
  <Content>
    <Wrapper>
      <H2 center>{getHeading(exampleView, listDateInPast)}</H2>
      <TimelineItems
        exampleView={exampleView}
        className={exampleView && "chromatic-ignore"}
      >
        {exampleView && <FadeoutOverlay exampleView={exampleView} />}
        <Stage
          data-test="stage-engage-agent"
          title="Choose an estate agent"
          stageInformation="Most good agents will be able to value, photograph and get your property listed on Rightmove and Zoopla within a week of getting in touch."
          linkToHomeText="Get a free valuation from Nested."
          dateText="Pick your agent by:"
          date={dates.engagedAgent}
          durationText="This step will take:"
          duration={duration.engagedAgent}
          icon="EngageAgent"
          exampleView={exampleView}
        />
        <Stage
          data-test="stage-listed"
          title="Finding a buyer"
          stageInformation={`Once your home is on the market, viewings will begin and it takes an average of ${Math.round(
            duration.listed,
          )} days for a home in your area to go Under Offer.`}
          dateText="List on the market by:"
          date={dates.listed}
          durationText="This usually takes:"
          duration={duration.listed}
          icon="Listing"
          exampleView={exampleView}
        />
        <Stage
          data-test="stage-offer-accepted"
          title="You are under offer"
          stageInformation={`After accepting an offer for your home, it takes ${Math.round(
            duration.offerAccepted,
          )} days on average to reach the Exchange of Contracts. Nested have less than half as many offers fall-through as the average estate agent.`}
          dateText="Expect to be under offer by:"
          date={dates.offerAccepted}
          durationText="Finalising your sale will take:"
          duration={duration.offerAccepted}
          icon="ProceedableOffer"
          exampleView={exampleView}
        />
        <Stage
          data-test="stage-exchange"
          title="Exchange of contracts"
          stageInformation="You're on the home straight now! After exchanging it's common to have two weeks to organise the final pieces before you complete the home sale and move into your new home."
          dateText="Exchange contracts by:"
          date={dates.exchange}
          durationText="Time before your move out:"
          duration={duration.exchange}
          icon="Exchange"
          exampleView={exampleView}
        />
        <Stage
          data-test="stage-completion"
          title="Complete your home sale"
          stageInformation="Congratulations! Your home sale is complete and you’re eating a takeaway on the floor in your new home tonight."
          dateText="Your completion date:"
          date={dates.completion}
          icon="Completion"
          exampleView={exampleView}
        />
      </TimelineItems>
      {exampleView && <CalculateCTA />}
    </Wrapper>
  </Content>
);

export default Timeline;
